import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession } from '../api/client';
import { CheckCircle, Zap, Star, Building2, ArrowLeft, Loader } from 'lucide-react';

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 29,
    icon: Zap,
    iconColor: '#F59E0B',
    iconBg: '#FEF3C7',
    highlight: false,
    features: [
      '3 technicians',
      '100 work orders / month',
      'Parts inventory',
      'Invoice generation',
      'AI diagnostics',
      'Email support',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: 59,
    icon: Star,
    iconColor: '#2563EB',
    iconBg: '#DBEAFE',
    highlight: true,
    badge: 'Most Popular',
    features: [
      '10 technicians',
      '1,000 work orders / month',
      'Everything in Starter',
      'GPS technician tracking',
      'Advanced reporting',
      'Priority support',
    ],
  },
  {
    key: 'business',
    name: 'Business',
    price: 99,
    icon: Building2,
    iconColor: '#7C3AED',
    iconBg: '#EDE9FE',
    highlight: false,
    features: [
      'Unlimited technicians',
      'Unlimited work orders',
      'Everything in Pro',
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantee',
    ],
  },
];

export default function Upgrade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['admin', 'manager'].includes(user.role);

  const handleSelect = async (planKey) => {
    if (!isAdmin) {
      setError('Only admins can manage subscriptions.');
      return;
    }
    setLoading(planKey);
    setError('');
    try {
      const res = await createCheckoutSession(planKey);
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading('');
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => navigate('/billing')}
          style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, marginBottom: 16 }}
        >
          <ArrowLeft size={15} /> Back to Billing
        </button>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
          Choose Your Plan
        </h1>
        <p style={{ color: '#6B7280', fontSize: 15 }}>
          All plans include a 14-day free trial. Cancel anytime.
        </p>
      </div>

      {error && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626',
          padding: '12px 16px', borderRadius: 8, marginBottom: 24, fontSize: 14,
        }}>
          {error}
        </div>
      )}

      {/* Plan Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 20 }}>
        {PLANS.map(plan => {
          const Icon = plan.icon;
          const isLoading = loading === plan.key;

          return (
            <div
              key={plan.key}
              style={{
                background: '#fff',
                borderRadius: 14,
                padding: 28,
                border: plan.highlight ? '2px solid #2563EB' : '1px solid #E5E7EB',
                boxShadow: plan.highlight
                  ? '0 8px 32px rgba(37,99,235,0.15)'
                  : '0 1px 3px rgba(0,0,0,0.06)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Badge */}
              {plan.badge && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#2563EB', color: '#fff', padding: '4px 14px',
                  borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                }}>
                  {plan.badge}
                </div>
              )}

              {/* Icon + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ background: plan.iconBg, borderRadius: 10, padding: 10 }}>
                  <Icon size={22} color={plan.iconColor} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>{plan.name}</div>
                </div>
              </div>

              {/* Price */}
              <div style={{ marginBottom: 24 }}>
                <span style={{ fontSize: 40, fontWeight: 800, color: '#111827' }}>${plan.price}</span>
                <span style={{ fontSize: 14, color: '#6B7280' }}>/month</span>
              </div>

              {/* Features */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px 0', flex: 1 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 14, color: '#374151' }}>
                    <CheckCircle size={14} color="#10B981" style={{ flexShrink: 0 }} /> {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleSelect(plan.key)}
                disabled={!!loading}
                style={{
                  width: '100%', padding: '12px',
                  background: plan.highlight ? '#2563EB' : '#F9FAFB',
                  color: plan.highlight ? '#fff' : '#111827',
                  border: plan.highlight ? 'none' : '1.5px solid #D1D5DB',
                  borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading && !isLoading ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {isLoading ? (
                  <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Redirecting...</>
                ) : (
                  <>Get Started with {plan.name}</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, marginTop: 28 }}>
        Secure checkout powered by Stripe · Cancel anytime · No hidden fees
      </p>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
